import { createElement, useContext, useEffect, useId, useState } from 'react'
import {
  MINIAPP_ICON_DEFAULT_LUCIDE,
  tilePixelSize,
  type MiniappTileGeometry,
  PDAPP_GLYPH_NODE,
  glyphStrokeStyle,
  iconSpecHash,
  type MiniappIconSpec,
} from '@papan/types'
// #788 — the ONE emitter and the ONE resolved model, shared with the PDApp SDK.
import { renderIconSvgString, resolveIconRenderModel } from '@papan/types/miniapp-icon-svg'
import { IconAssetUrlContext } from './IconAssetUrlContext'

// ---------------------------------------------------------------------------
// Font lazy-loader (FontFace API)
// ---------------------------------------------------------------------------
//
// The Noto emoji woff2 files (~1.5–2 MB for color) are vendored under
// apps/web/public/fonts/noto-emoji/ and loaded on demand via the FontFace API
// — NEVER via @font-face in the global CSS — so non-icon users never pay the
// bytes. Idempotent, cached per style at module scope, no-op on SSR / where
// FontFace is unavailable.
//
// Safari lacks COLRv1; the stack falls back to system emoji — the font-family
// stack below handles that engine transparently, no feature-detect needed.

const FONT_URLS = {
  color: {
    family: 'Noto Color Emoji',
    url: "url(/fonts/noto-emoji/NotoColorEmoji.woff2) format('woff2')",
  },
  mono: {
    family: 'Noto Emoji',
    url: "url(/fonts/noto-emoji/NotoEmoji.woff2) format('woff2')",
  },
} as const

const notoLoads: Partial<Record<'color' | 'mono', Promise<void>>> = {}

/** Load a Noto emoji face once per style (idempotent, SSR-safe). */
export function ensureNotoEmoji(style: 'color' | 'mono'): Promise<void> {
  const cached = notoLoads[style]
  if (cached) return cached
  const load = (async () => {
    if (typeof document === 'undefined' || typeof FontFace === 'undefined') return
    try {
      const { family, url } = FONT_URLS[style]
      const face = new FontFace(family, url)
      await face.load()
      document.fonts.add(face)
    } catch {
      // Load failure (offline, missing asset, Safari COLRv1) is non-fatal —
      // the font-family fallback stack renders system emoji instead.
    }
  })()
  notoLoads[style] = load
  return load
}

/** Cheap heuristic: does the text contain a glyph best served by an emoji font? */
function hasEmoji(text: string): boolean {
  return /[\u{1F000}-\u{FFFFF}☀-➿]/u.test(text)
}

// ---------------------------------------------------------------------------
// Lucide icon nodes (lazy, code-split, cached)
// ---------------------------------------------------------------------------
//
// A spec's `icon` is a Lucide name (e.g. `layout-grid`). We render the raw
// icon geometry INLINE inside our 512-grid SVG (not a nested <svg>) so it clips
// to the squircle and scales with `size`. lucide-react ships each icon as an
// `__iconNode` (`[tag, attrs][]`); we import it on demand via the same
// `dynamicIconImports` map lucide's own DynamicIcon uses, cache it at module
// scope, and seed the DEFAULT (`box`) so the common case — every
// mini-app with no custom icon — paints synchronously with no async flash.

// lucide-react ships each icon's geometry under a name that has CHANGED:
// `__iconNode` (the array) up to 1.46, `__iconData` (`{ name, size, node }`) from
// 1.47 — `ensureLucideIconNode` reads both, see there. Historically each entry
// was `[tag, attrs]`; since lucide 1.4x each entry is `[tag, attrs, children?]`
// (nested geometry). We render only tag + attrs inline (see the renderer below),
// so the optional third element is accepted and ignored — and keeping it here
// restores the overlap the `ensureLucideIconNode` cast needs against lucide's
// current exported node type (bumped 1.24 → 1.47).
type IconNodeEntry = [tag: string, attrs: Record<string, string | number>, children?: unknown]
type IconNode = IconNodeEntry[]

// lucide 1.47 stopped exporting `__iconNode` from each icon module; the geometry
// moved to `__iconData.node`. Reading only the old name made EVERY dynamic load
// look like an unknown icon, so every custom icon was cached as the default mark.
// Accept both shapes so a lucide bump in either direction can't blank them again.
type LucideIconModule = { __iconNode?: IconNode; __iconData?: { node?: IconNode } }
// The server resolver (`features/files/core/lucide-node.ts`) accepts both too.
function lucideModuleNode(mod: LucideIconModule | undefined): IconNode | null {
  if (Array.isArray(mod?.__iconNode)) return mod.__iconNode
  if (Array.isArray(mod?.__iconData?.node)) return mod.__iconData.node
  return null
}

const iconNodeCache = new Map<string, IconNode>()

// Seed the default inline (its geometry is tiny and stable) so drive rows that
// render the fallback icon never flash empty while a dynamic import resolves.
// This IS what the default renders (the seed short-circuits the async load).
// It is the reserved PDApp mark, NOT a Lucide icon — the seed is also what makes
// `pdapp` resolvable at all, since no dynamic import could ever supply it.
iconNodeCache.set(MINIAPP_ICON_DEFAULT_LUCIDE, PDAPP_GLYPH_NODE)

/**
 * Load a Lucide icon's raw geometry and cache it under `name` (idempotent).
 * Resolves once the cache is populated so a caller can `await` it and then read
 * `iconNodeCache.get(name)` SYNCHRONOUSLY — this is what lets the string renderer
 * (`renderMiniappIconSvg`) draw a custom icon without an async gap.
 *
 * An UNKNOWN name (no loader) is definitively cached as the seeded default glyph
 * so it's not re-imported. A TRANSIENT import failure is NOT cached (a retry
 * should re-attempt the real icon) — matching the server renderer (icon-render.ts)
 * and the schema, which only checks `icon`'s kebab-case format, not its existence.
 */
export async function ensureLucideIconNode(name: string): Promise<void> {
  if (iconNodeCache.has(name)) return
  const fallback = iconNodeCache.get(MINIAPP_ICON_DEFAULT_LUCIDE) ?? null
  try {
    const m = await import('lucide-react/dynamicIconImports')
    // Narrow lucide's module to the slice we consume, via `unknown`: its exported
    // node type is the full `[tag, Partial<SVGProps>, children[]]` (booleans and
    // nested geometry included), but we render only tag + stringified attrs
    // inline, so `IconNode` above is deliberately the narrower renderer shape.
    const map = (m as unknown as { default?: Record<string, () => Promise<LucideIconModule>> }).default ?? m
    const loader = (map as unknown as Record<string, () => Promise<LucideIconModule>>)[name]
    const mod = typeof loader === 'function' ? await loader() : undefined
    const n = lucideModuleNode(mod)
    if (n) iconNodeCache.set(name, n)
    else if (fallback) iconNodeCache.set(name, fallback) // definitively unknown → cache the default
  } catch {
    // Transient import failure — leave the cache untouched so a retry re-attempts.
  }
}

/** Resolve a Lucide icon's raw node by name (cached; null until an async load lands).
 *  An UNKNOWN or unloadable name falls back to the seeded default glyph — mirrors
 *  the server renderer (icon-render.ts) so a bad/typo'd `icon` (which only passes
 *  the schema's kebab-case format check, not existence) renders the default tile
 *  instead of a blank square. */
function useIconNode(name: string): IconNode | null {
  const [node, setNode] = useState<IconNode | null>(() => iconNodeCache.get(name) ?? null)
  useEffect(() => {
    const cached = iconNodeCache.get(name)
    if (cached) { setNode(cached); return }
    setNode(null)
    let alive = true
    const fallback = iconNodeCache.get(MINIAPP_ICON_DEFAULT_LUCIDE) ?? null
    void ensureLucideIconNode(name).then(() => {
      if (!alive) return
      // Populated cache (real geometry, or the default for an unknown name) wins;
      // a transient failure left the cache empty → render the default this pass.
      const n = iconNodeCache.get(name) ?? fallback
      if (n) setNode(n)
    })
    return () => { alive = false }
  }, [name])
  return node
}

// The glyph box comes from `tileGeometry` (it differs per tile shape — the
// portrait tile sizes it off the NARROW axis). Lucide art is on a 24-unit grid.
function glyphTransform(geom: MiniappTileGeometry): string {
  return `translate(${geom.glyph.x},${geom.glyph.y}) scale(${geom.glyph.dim / 24})`
}

/** Inline <g> of a Lucide icon's paths, colored + scaled into the 512 grid. */
/** Which glyph a resolved node actually IS. An unknown name is cached AS the
 *  default node (see `useIconNode` / `ensureLucideIconNode`), so the stroke style
 *  has to follow the GEOMETRY that resolved, not the name that was asked for —
 *  otherwise a typo'd name falls back to the flat-capped PDApp mark and then
 *  draws it with Lucide's round caps, quietly erasing the mark's corners. */
function drawnGlyphName(node: IconNode, requested: string): string {
  return node === iconNodeCache.get(MINIAPP_ICON_DEFAULT_LUCIDE) ? MINIAPP_ICON_DEFAULT_LUCIDE : requested
}

function LucideGlyph({ name, color, geom }: { name: string; color: string; geom: MiniappTileGeometry }) {
  const node = useIconNode(name)
  if (!node) return null
  const stroke = glyphStrokeStyle(drawnGlyphName(node, name))
  return (
    <g
      transform={glyphTransform(geom)}
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap={stroke.cap}
      strokeLinejoin={stroke.join}
    >
      {node.map((entry, i) => {
        const [tag, attrs] = entry
        const { key: _k, ...rest } = attrs as Record<string, unknown>
        return createElement(tag, { key: i, ...rest })
      })}
    </g>
  )
}

// ---------------------------------------------------------------------------
// Shared render model — the SINGLE resolution of a spec into draw inputs, used
// by BOTH the <MiniappIcon> React component and the renderMiniappIconSvg string
// renderer so the two can never drift. Pure (no DOM, no color validation) so the
// React path stays byte-identical; the string path layers XML-escaping + color
// validation on top when it serializes (see below).
// ---------------------------------------------------------------------------

/**
 * Render a mini-app icon spec to a self-contained SVG STRING — the same picture
 * as <MiniappIcon spec name size>, for innerHTML callers.
 *
 * A THIN ADAPTER since #788: the drawing itself is `renderIconSvgString` in
 * `@papan/types`, which the PDApp SDK also calls, so the two cannot draw
 * differently. This side supplies what the shared emitter refuses to fetch for
 * itself — the Lucide geometry out of `iconNodeCache` (read synchronously; an
 * unresolved custom icon falls back to the always-seeded default) and the emoji
 * font load.
 *
 * ASSET-bearing icons (image bg / image fg) render only their spec PLACEHOLDER
 * here — the real pixels live in the server-composited PNG, which needs an <img>
 * (and a `refId`) this string has no way to await. Callers that must show the
 * true asset icon should mount <MiniappIcon> instead.
 */
/** The node for `name` ONLY when it is genuinely that glyph — not the default
 *  node cached under an unknown name. Mirrors `drawnGlyphName`'s identity test. */
function resolvedCustomNode(name: string): IconNode | null {
  const node = iconNodeCache.get(name) ?? null
  return node && node !== iconNodeCache.get(MINIAPP_ICON_DEFAULT_LUCIDE) ? node : null
}

export function renderMiniappIconSvg(opts: {
  spec: MiniappIconSpec | null
  name: string
  size: number
}): string {
  const { spec, name, size } = opts
  const model = resolveIconRenderModel(spec, name)
  // Trigger the emoji font load (font swap is acceptable — matches the component).
  if (!model.iconName && hasEmoji(model.text)) void ensureNotoEmoji(model.emojiStyle)
  return renderIconSvgString({
    spec,
    name,
    size,
    // NODE IDENTITY, not the name that was asked for. An unknown name is cached
    // AS the default node (`ensureLucideIconNode`), so passing it through as a
    // resolved custom glyph makes the emitter style it like one — the flat-capped
    // PDApp mark drawn with Lucide's round caps, its corners quietly erased. This
    // is what `drawnGlyphName` does for the React component; handing the emitter
    // `null` is the same answer in the shape the emitter takes.
    iconNode: model.iconName ? resolvedCustomNode(model.iconName) : null,
    defaultIconNode: iconNodeCache.get(MINIAPP_ICON_DEFAULT_LUCIDE) ?? null,
  })
}

/**
 * The custom glyph geometry a PURE renderer needs handed to it (#788).
 *
 * `pdappIconByMetadata` in the SDK cannot import Lucide — it draws whatever the
 * metadata carries, so a named glyph has to arrive as `icon.glyph`. The server
 * export resolves it with `resolveLucideNode`; an icon DESIGNED in the browser
 * resolves it here, through the SAME node-identity rule the component uses, so
 * the two paths cannot draw different pictures.
 *
 * `null` means "no custom geometry" — either the spec has no Lucide foreground,
 * or the name is unknown and resolved AS the default mark. What a producer then
 * puts in the metadata is `portableGlyphFor`'s (`@papan/types/miniapp-icon-svg`)
 * to say, so the browser and the server export cannot answer it differently;
 * both of the obvious answers draw an empty tile, and the reasons are stated
 * there.
 *
 * THROWS when the load failed transiently, rather than answering `null`. Papan's
 * own renderer recovers from a dropped chunk on the next mount; metadata is a
 * COPY and never re-resolves, so answering `null` here would bake the reserved
 * mark into someone's page permanently over one bad request. The cache already
 * distinguishes the two — a definitively unknown name is stored AS the default,
 * a transient failure leaves nothing — which is what makes this answerable.
 */
export async function resolveMiniappIconGlyph(
  spec: MiniappIconSpec | null,
  name: string,
): Promise<IconNode | null> {
  const { iconName } = resolveIconRenderModel(spec, name)
  if (!iconName) return null
  await ensureLucideIconNode(iconName)
  if (!iconNodeCache.has(iconName)) {
    throw new Error(`The “${iconName}” icon could not be loaded. Check your connection and try again.`)
  }
  return resolvedCustomNode(iconName)
}

/**
 * Pre-resolve the async assets `renderMiniappIconSvg` needs to draw `spec` at
 * full fidelity SYNCHRONOUSLY: a custom Lucide icon's geometry (→ iconNodeCache)
 * and, for an emoji text spec, the Noto face. Idempotent; safe to await before
 * emitting the SVG string so a custom icon paints immediately (no default-box
 * flash). Uses the SAME resolution as the renderer so they can't disagree.
 */
export async function ensureMiniappIconAssets(spec: MiniappIconSpec | null, name: string): Promise<void> {
  const { iconName, text, emojiStyle } = resolveIconRenderModel(spec, name)
  if (iconName) await ensureLucideIconNode(iconName)
  else if (hasEmoji(text)) await ensureNotoEmoji(emojiStyle)
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

export interface MiniappIconProps {
  /** Stored spec; when null/undefined a name-derived default is rendered. */
  spec?: MiniappIconSpec | null
  /** Folder/display name — drives the default spec fallback. */
  name: string
  /** Rendered edge length in px (square). */
  size: number
  className?: string
  /** The `.miniapp` folder id — required to load an ASSET icon's server PNG. */
  refId?: string | null
  /** The icon's cache-bust rev (`parseMiniappIconRev(metadataJson)`) — pairs with
   *  `refId` to fetch/cache the asset PNG. OPTIONAL: without it the spec hash is
   *  used instead, which still renders the CURRENT icon (the server treats this
   *  token as a cache-buster only) but can't be cached immutably, since replacing
   *  an asset doesn't change the spec. Pass it whenever the row carries it. */
  rev?: string | null
  /** EDITOR ONLY: local object-URL images to composite client-side instead of
   *  fetching the server PNG — staged (not yet uploaded) assets AND already-saved
   *  ones the editor re-fetched. Passing this AT ALL (even `{bg:null,fg:null}`)
   *  opts out of the server PNG entirely, which is what an editor wants: the server
   *  PNG is the SAVED render and its `rev` can't track an in-progress draft, so
   *  using it would freeze the preview. Each slot is still gated on the DRAFT spec
   *  below, so a URL for a slot the draft doesn't reference is ignored. */
  previewAssets?: { bg?: string | null; fg?: string | null }
}

/** Pick a server render size (∈ {64,180,512}) ≥ the CSS size × devicePixelRatio. */
function pickServerSize(cssSize: number): number {
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 1
  const needed = cssSize * dpr
  return needed <= 64 ? 64 : needed <= 180 ? 180 : 512
}

/**
 * The ONE renderer for a mini-app icon across every client surface.
 *
 * Spec-only icons (solid/gradient bg + text/lucide/blank foreground) draw an SVG
 * on a 512-grid `viewBox`, clipped to the shared squircle. ASSET-bearing icons
 * (custom image background or uploaded foreground) render the server-composited
 * PNG via `<img>` — resolved through `IconAssetUrlContext` from `refId` + `rev`;
 * until it loads (or with no resolver/refId) a spec placeholder (background, no
 * glyph) shows so nothing breaks.
 */
export function MiniappIcon({ spec, name, size, className, refId, rev, previewAssets }: MiniappIconProps) {
  // Shared with renderMiniappIconSvg so the component and the string renderer
  // can't drift (same silhouette/gradient/text/glyph/blank resolution).
  const { geom, resolved, isAsset, bg, emojiStyle, fill, text, iconName, fontFamily, layout } =
    resolveIconRenderModel(spec, name)
  // `size` is the tile's HEIGHT; a portrait tile narrows rather than pushing
  // every row that reserved a square slot taller.
  const px = tilePixelSize(geom.shape, size)
  const localPreview = !!previewAssets

  // ── Asset icons → server-composited PNG via <img> (skipped in editor preview) ─
  const assetCtx = useContext(IconAssetUrlContext)
  const [assetSrc, setAssetSrc] = useState<string | null>(null)
  // Missing `rev` must NOT mean "don't render the custom icon" — that silently
  // downgraded every surface whose row didn't thread the rev through to a
  // name-derived gradient, which looks like the user's icon simply not working.
  // The spec hash is a safe stand-in: the token only busts caches, so the image is
  // still the current one; it just can't be `immutable`.
  const cacheToken = rev || iconSpecHash(resolved)
  useEffect(() => {
    if (localPreview || !isAsset || !refId || !assetCtx) { setAssetSrc(null); return }
    let alive = true
    assetCtx.load(refId, cacheToken, pickServerSize(size)).then((url) => { if (alive) setAssetSrc(url) }).catch(() => {})
    return () => { alive = false }
  }, [localPreview, isAsset, refId, cacheToken, assetCtx, size])

  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const clipId = `miniapp-clip-${uid}`
  const gradId = `miniapp-grad-${uid}`

  // Only pay the emoji font load when text actually contains emoji.
  useEffect(() => {
    if (!iconName && hasEmoji(text)) void ensureNotoEmoji(emojiStyle)
  }, [iconName, text, emojiStyle])

  // The server PNG bakes in the squircle, so a loaded asset needs no clip.
  if (isAsset && assetSrc) {
    return (
      <img
        src={assetSrc}
        width={px.w}
        height={px.h}
        className={className}
        alt={name}
        style={{ display: 'block', objectFit: 'contain' }}
        onError={() => setAssetSrc(null)}
      />
    )
  }

  // Wrap + auto-fit multi-line text layout comes from the shared model above.
  const lineCount = layout ? layout.lines.length : 0
  const firstLineY = layout ? geom.h / 2 - ((lineCount - 1) / 2) * layout.lineHeight : geom.h / 2

  return (
    <svg
      viewBox={`0 0 ${geom.w} ${geom.h}`}
      width={px.w}
      height={px.h}
      className={className}
      role="img"
      aria-label={name}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={geom.clipPath} />
        </clipPath>
        {bg.type === 'gradient' && (
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={bg.from} />
            {/* Absent ⇒ 1 (even ramp); below 1 the remainder is solid `to`. */}
            <stop offset={bg.toStop ?? 1} stopColor={bg.to} />
          </linearGradient>
        )}
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {previewAssets?.bg && resolved.bg.type === 'image' ? (
          <image href={previewAssets.bg} x="0" y="0" width={geom.w} height={geom.h} preserveAspectRatio="xMidYMid slice" />
        ) : (
          <rect
            x="0"
            y="0"
            width={geom.w}
            height={geom.h}
            fill={bg.type === 'solid' ? bg.color : `url(#${gradId})`}
          />
        )}
        {previewAssets?.fg && resolved.image === true ? (
          <image href={previewAssets.fg} x={geom.image.x} y={geom.image.y} width={geom.image.dim} height={geom.image.dim} preserveAspectRatio="xMidYMid meet" />
        ) : iconName ? (
          <LucideGlyph name={iconName} color={fill} geom={geom} />
        ) : layout && layout.lines.length > 0 ? (
          <text
            textAnchor="middle"
            fontFamily={fontFamily}
            fontWeight={600}
            fontSize={layout.fontSize}
            fill={fill}
          >
            {layout.lines.map((line, i) => (
              <tspan key={i} x={geom.w / 2} y={firstLineY + i * layout.lineHeight} dominantBaseline="central">
                {line}
              </tspan>
            ))}
          </text>
        ) : null}
      </g>
    </svg>
  )
}
